export const ANALYSIS_SCHEMA_VERSION = '1.0.0' as const;

export type AnalysisSchemaVersion = typeof ANALYSIS_SCHEMA_VERSION;

export type ProjectPathKind = 'file' | 'directory';

export type RuleKind = 'pattern' | 'comment' | 'blank';

export type RuleStatus =
  | 'Active'
  | 'Matched'
  | 'Unmatched'
  | 'Overridden'
  | 'Blocked negation'
  | 'Broad rule'
  | 'Comment'
  | 'Blank';

export type MatchState = 'ignored' | 'tracked';

export type WarningSeverity = 'warning' | 'info';

export type WarningCode =
  'blocked-negation' | 'broad-rule' | 'sensitive-path-unignored' | 'sensitive-path-reincluded';

export type InputIssueCode =
  | 'absolute-path'
  | 'path-traversal'
  | 'empty-path'
  | 'invalid-path'
  | 'duplicate-path'
  | 'invalid-source-path'
  | 'duplicate-source';

export interface GitignoreSourceInput {
  /** Repository-relative path to this ignore file, for example `.gitignore`. */
  readonly path: string;
  readonly content: string;
}

export interface AnalyzeProjectInput {
  readonly sources: readonly GitignoreSourceInput[];
  readonly paths: readonly string[];
  /** Injectable clock used by stable reports and tests. */
  readonly now?: Date | string | number;
}

export interface PathInputIssue {
  readonly code: InputIssueCode;
  readonly input: string;
  readonly message: string;
}

export interface NormalizedProjectPath {
  readonly path: string;
  readonly kind: ProjectPathKind;
  readonly inferred: boolean;
}

export interface ParsedRule {
  readonly id: string;
  readonly source: string;
  readonly sourceDirectory: string;
  readonly line: number;
  readonly raw: string;
  /** Raw pattern as consumed by `ignore`; empty for comments and blank lines. */
  readonly pattern: string;
  readonly kind: RuleKind;
  readonly negated: boolean;
  readonly directoryOnly: boolean;
  readonly anchored: boolean;
  readonly containsSlash: boolean;
  readonly trailingSpaceCount: number;
  readonly escapedTrailingSpace: boolean;
}

export interface ParsedGitignoreSource {
  readonly path: string;
  readonly directory: string;
  readonly content: string;
  readonly rules: readonly ParsedRule[];
}

export interface RuleReference {
  readonly id: string;
  readonly source: string;
  readonly line: number;
  readonly raw: string;
  readonly pattern: string;
  readonly negated: boolean;
}

export interface MatchedRule extends RuleReference {
  readonly stateAfter: MatchState;
  readonly decisive: boolean;
  readonly blocked: boolean;
  readonly explanation: string;
}

export interface AnalysisWarning {
  readonly id: string;
  readonly code: WarningCode;
  readonly severity: WarningSeverity;
  readonly message: string;
  readonly reason: string;
  readonly paths: readonly string[];
  readonly source: string | null;
  readonly line: number | null;
  readonly suggestedRule: string | null;
  readonly suggestedSource: string | null;
}

export interface PathAnalysis {
  readonly path: string;
  readonly kind: ProjectPathKind;
  readonly inferred: boolean;
  readonly ignored: boolean;
  readonly decisiveRule: RuleReference | null;
  readonly matchedRules: readonly MatchedRule[];
  readonly ruleSource: string | null;
  readonly ruleLine: number | null;
  readonly explanation: string;
  readonly warnings: readonly AnalysisWarning[];
}

export interface RuleAnalysis extends ParsedRule {
  /** Principal status used for compact rule-list rendering. */
  readonly status: RuleStatus;
  /** All applicable statuses; broadness is additive to the lifecycle status. */
  readonly statuses: readonly RuleStatus[];
  readonly matchCount: number;
  readonly decisiveCount: number;
  readonly blockedCount: number;
  readonly affectedPaths: readonly string[];
}

export interface SourceAnalysis {
  readonly path: string;
  readonly directory: string;
  readonly ruleIds: readonly string[];
}

export interface AnalysisSummary {
  readonly pathCount: number;
  readonly explicitPathCount: number;
  readonly inferredPathCount: number;
  readonly ignoredCount: number;
  readonly trackedCount: number;
  readonly warningCount: number;
  readonly sourceCount: number;
  readonly ruleCount: number;
}

export interface AnalysisReport {
  readonly schemaVersion: AnalysisSchemaVersion;
  readonly generatedAt: string;
  readonly summary: AnalysisSummary;
  readonly sources: readonly SourceAnalysis[];
  readonly rules: readonly RuleAnalysis[];
  readonly paths: readonly PathAnalysis[];
  readonly warnings: readonly AnalysisWarning[];
  readonly inputIssues: readonly PathInputIssue[];
}
